import { log } from '../log/log.web';
// Use explicit `.ts` so Vite's platform-extension resolver
// (`['.web.ts', '.ts', ...]`) does not collapse `'./identity'` back
// into `identity.web.ts` (this file) and produce a circular import.
import type { IdentityHandle, IdentityManager } from './identity.ts';
import { IdentityCorruptionError, IdentityKeyUnavailableError } from './identity.ts';
import { openDB } from 'idb';

export type { IdentityHandle, IdentityManager } from './identity.ts';
export { IdentityCorruptionError, IdentityKeyUnavailableError } from './identity.ts';

const DB_NAME = 'ping-identity';
const STORE_NAME = 'wrapped-identity';
const RECORD_KEY = 'identity';
// Persistent slot for the AES wrap key. Stored as a non-extractable
// `CryptoKey` — browsers structured-clone CryptoKeys into IndexedDB
// natively, so the key handle survives reloads without ever being
// extractable to userland.
//
// Before this slot existed the wrap key was generated fresh on every
// page load via `crypto.subtle.generateKey({...}, false, ...)`, and the
// previous load's ciphertext was always "corrupted" against the new
// key — the SDK's identity got wiped on every reload, breaking access
// to any pre-existing conversation (the freshly-regenerated device
// isn't a member of those groups, so `POST /v1/messages` 404s and
// `GET /v1/conversations` 401s on a stale token).
const WRAP_KEY_RECORD = 'wrap-key';
const ALGORITHM = { name: 'AES-GCM', length: 256 };
const IV_LENGTH = 12;

interface WrappedIdentityRecord {
  ciphertext: ArrayBuffer;
  iv: Uint8Array;
}

let wrapKey: CryptoKey | null = null;

async function getDB() {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  });
}

// getOrCreateWrapKey returns the persistent AES wrap key.
//
// `allowCreate` controls what happens when the persisted key record is absent:
//   - true  (wrapping a NEW identity): mint a fresh key and persist it.
//   - false (unwrapping EXISTING ciphertext): DO NOT mint a new key. A fresh key
//     could never decrypt ciphertext written under the old one, so silently
//     generating one here manufactures a bogus "IdentityCorruptionError" out of
//     a transient wrap-key eviction (Safari/ITP evicting just this record, quota
//     pressure) and triggers a wrongful full wipe. Instead we throw
//     IdentityKeyUnavailableError so the caller preserves credentials and retries.
async function getOrCreateWrapKey(allowCreate: boolean): Promise<CryptoKey> {
  if (wrapKey) {
    return wrapKey;
  }

  const db = await getDB();
  const stored = await db.get(STORE_NAME, WRAP_KEY_RECORD);
  if (stored !== undefined && stored !== null && typeof stored === 'object' && 'algorithm' in stored) {
    // Browser structured-cloned a real CryptoKey on first put; surfaces
    // here as the same handle (non-extractable, AES-GCM-256).
    wrapKey = stored as CryptoKey;
    log.debug('identity.wrapKey.loaded', { algorithm: 'AES-GCM-256' });
    return wrapKey;
  }

  if (!allowCreate) {
    // Key record missing while we were asked to DECRYPT — do not overwrite the
    // slot with a new (useless) key. Signal the transient, recoverable state.
    log.error('identity.wrapKey.unavailable');
    throw new IdentityKeyUnavailableError('wrap key record missing; cannot decrypt existing identity');
  }

  wrapKey = await crypto.subtle.generateKey(
    ALGORITHM,
    false, // non-extractable
    ['encrypt', 'decrypt'],
  );
  await db.put(STORE_NAME, wrapKey, WRAP_KEY_RECORD);

  log.debug('identity.wrapKey.generated', { algorithm: 'AES-GCM-256' });
  return wrapKey;
}

async function generateIdentity(): Promise<IdentityHandle> {
  const seed = crypto.getRandomValues(new Uint8Array(32));
  log.debug('identity.generated', { byteCount: seed.length });

  return {
    _brand: 'IdentityHandle' as const,
    identityExport: seed,
  };
}

async function wrapIdentity(seed: Uint8Array): Promise<void> {
  const key = await getOrCreateWrapKey(true);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Encrypt the seed as opaque data. We previously used wrapKey() which
  // required the seed to BE a 128/256-bit AES key — that broke when the
  // SDK started returning CBOR-encoded identity exports of arbitrary
  // length. `encrypt` accepts any byte array.
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    seed.buffer as ArrayBuffer,
  );

  const db = await getDB();
  await db.put(STORE_NAME, { ciphertext, iv }, RECORD_KEY);

  log.debug('identity.wrapped', {
    ciphertextByteCount: ciphertext.byteLength,
    ivByteCount: iv.length,
  });
}

async function unwrapIdentity(): Promise<Uint8Array | null> {
  try {
    const db = await getDB();
    const record = await db.get(STORE_NAME, RECORD_KEY) as WrappedIdentityRecord | undefined;

    if (!record) {
      log.debug('identity.unwrap.notFound');
      return null;
    }

    // allowCreate:false — the ciphertext exists, so a missing wrap key is a
    // transient eviction, NOT license to mint a new (useless) key. Throws
    // IdentityKeyUnavailableError, which we let propagate below.
    const key = await getOrCreateWrapKey(false);

    try {
      const plaintext = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: record.iv as BufferSource,
        },
        key,
        record.ciphertext as BufferSource,
      );

      const seed = new Uint8Array(plaintext);

      log.debug('identity.unwrapped', { byteCount: seed.length });
      return seed;
    } catch (error) {
      // Key WAS present but decrypt still failed → genuine corruption/tamper.
      log.error('identity.unwrap.corrupted', error instanceof Error ? error : undefined);
      throw new IdentityCorruptionError('Failed to unwrap identity: ciphertext corrupted or tampered');
    }
  } catch (error) {
    // Propagate both typed errors so the bootstrap can distinguish them; only a
    // genuinely unexpected error (e.g. IDB open failure) falls through to null.
    if (error instanceof IdentityCorruptionError || error instanceof IdentityKeyUnavailableError) {
      throw error;
    }
    log.error('identity.unwrap.failed', error instanceof Error ? error : undefined);
    return null;
  }
}

async function forgetIdentity(): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, RECORD_KEY);
  // Wipe the persisted wrap key too. Without this, any *new* identity
  // wrapped after `forgetIdentity` would reuse the old key — fine for
  // confidentiality but indistinguishable from "leftover state" during
  // QA / sign-out flows. Always start the next wrap from a fresh key.
  await db.delete(STORE_NAME, WRAP_KEY_RECORD);

  wrapKey = null;

  log.debug('identity.forgotten');
}

export const identityManager: IdentityManager = {
  generateIdentity,
  wrapIdentity,
  unwrapIdentity,
  forgetIdentity,
};
